import { useEffect, useMemo } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { AuthedImage } from '@/components/AuthedImage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { PhotoView } from '@/types/api';

export interface ItemPhotoSectionProps {
  title: string;
  /** Already uploaded, server-known photos. */
  photos: PhotoView[];
  /**
   * Picked but not yet uploaded (issue #131) — only ever non-empty while
   * creating a new item, where there's no id yet to upload against. Rendered
   * from a local object URL rather than through the authed `/raw` endpoint,
   * since the file was never sent to the server.
   */
  pending?: File[];
  addLabel: string;
  openLabel: string;
  removeLabel: string;
  onUpload: (file: File) => void;
  onRemove: (photoId: number) => void;
  onRemovePending?: (index: number) => void;
  onZoom: (url: string) => void;
}

/**
 * Shared grid + upload/remove/zoom UX for both the item-photo and
 * receipt-photo sections, on both ItemDetailPage (photos already exist) and
 * ItemFormPage (a new item may still have none saved yet — see [pending]).
 */
export function ItemPhotoSection({
  title,
  photos,
  pending = [],
  addLabel,
  openLabel,
  removeLabel,
  onUpload,
  onRemove,
  onRemovePending,
  onZoom,
}: ItemPhotoSectionProps) {
  // One object URL per pending file, revoked whenever the set changes (a
  // pick, a removal, or unmounting mid-edit) so a long create session
  // doesn't pile up blob: URLs the browser never reclaims on its own.
  const pendingPreviewUrls = useMemo(() => pending.map((file) => URL.createObjectURL(file)), [pending]);
  useEffect(() => () => pendingPreviewUrls.forEach((url) => URL.revokeObjectURL(url)), [pendingPreviewUrls]);

  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {photos.map((p) => (
            <div key={p.id} className="group relative overflow-hidden rounded-md border">
              <button
                type="button"
                aria-label={openLabel}
                className="block w-full"
                onClick={() => onZoom(p.url)}
              >
                <AuthedImage src={p.url} className="h-32 w-full object-cover" />
              </button>
              <button
                type="button"
                className="absolute right-1 top-1 rounded bg-background/80 p-1 opacity-0 transition focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                onClick={() => onRemove(p.id)}
                aria-label={removeLabel}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </button>
            </div>
          ))}
          {pending.map((file, index) => (
            <div key={`${file.name}-${index}`} className="group relative overflow-hidden rounded-md border">
              <img src={pendingPreviewUrls[index]} alt="" className="h-32 w-full object-cover" />
              <button
                type="button"
                className="absolute right-1 top-1 rounded bg-background/80 p-1 opacity-0 transition focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                onClick={() => onRemovePending?.(index)}
                aria-label={removeLabel}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </button>
            </div>
          ))}
          <label className="flex h-32 cursor-pointer items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground hover:bg-accent focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={(e) => {
                const input = e.currentTarget;
                const f = input.files?.[0];
                if (f) onUpload(f);
                // Reset so picking the same file again still fires change.
                input.value = '';
              }}
            />
            <span className="flex items-center gap-1"><Upload className="h-4 w-4" /> {addLabel}</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
